import React, { useMemo } from 'react';

import { useCheckAccess } from '@hooks';
import TableBody from './components/TableBody';
import TableEmptyState from './components/TableEmptyState';
import TableHeader from './components/TableHeader';
import TableLoader from './components/TableLoader';
import TableRoot from './components/TableRoot';
import type { ColumnMeta, ColumnType, StickySide, TableV2Props } from './types';

export type { ColumnType, ConfigType, TableV2Props } from './types';

const getColumnId = <T,>(column: ColumnType<T>, index: number) =>
  column.id ?? column.sortName ?? String(index);

const getNumericWidth = (width?: number | string | null) =>
  typeof width === 'number' ? width : undefined;

const getStickySide = <T,>(
  column: ColumnType<T>,
  index: number,
  stickyColumns?: TableV2Props<T>['stickyColumns']
): StickySide | undefined => {
  if (column.sticky === true) return 'left';
  if (column.sticky === 'left' || column.sticky === 'right') {
    return column.sticky;
  }

  if (typeof stickyColumns === 'number') {
    return index < stickyColumns ? 'left' : undefined;
  }

  if (Array.isArray(stickyColumns)) {
    const columnId = getColumnId(column, index);
    return stickyColumns.includes(columnId) || stickyColumns.includes(index)
      ? 'left'
      : undefined;
  }

  return undefined;
};

const buildColumnMeta = <T,>(
  columns: ColumnType<T>[],
  stickyColumns?: TableV2Props<T>['stickyColumns']
): ColumnMeta<T>[] => {
  const leftOffsets: number[] = [];
  const rightOffsets: number[] = [];
  let left = 0;
  let right = 0;

  const sides = columns.map((column, index) =>
    getStickySide(column, index, stickyColumns)
  );

  columns.forEach((column, index) => {
    leftOffsets[index] = left;
    if (sides[index] === 'left') {
      left += getNumericWidth(column.width ?? column.headerStyle?.width) ?? 0;
    }
  });

  for (let index = columns.length - 1; index >= 0; index -= 1) {
    const column = columns[index];
    rightOffsets[index] = right;
    if (sides[index] === 'right') {
      right += getNumericWidth(column.width ?? column.headerStyle?.width) ?? 0;
    }
  }

  return columns.map((column, index) => {
    const stickySide = sides[index];

    return {
      ...column,
      id: getColumnId(column, index),
      columnIndex: index,
      stickySide,
      stickyOffset:
        column.stickyOffset ??
        (stickySide === 'left'
          ? leftOffsets[index]
          : stickySide === 'right'
            ? rightOffsets[index]
            : undefined),
    };
  });
};

const TableV2 = <T,>({
  data = [],
  config: initialConfig,
  className,
  containerClassName,
  loading = false,
  withHeader = true,
  clampCells = true,
  cellsClassName,
  headersClassName,
  cellsContentClassName,
  verticalAlign = 'middle',
  emptyMessage = 'No items.',
  readonly = false,
  onRowClick,
  sort,
  onSort,
  stickyHeader = true,
  stickyColumns,
  getRowKey,
  getRowProps,
  getCellProps,
}: TableV2Props<T>): React.ReactNode => {
  const allowedColumns = useCheckAccess(initialConfig.columns);
  const columns = useMemo(
    () => buildColumnMeta(allowedColumns, stickyColumns),
    [allowedColumns, stickyColumns]
  );
  const isEmpty = !data?.length && !loading;

  return (
    <TableRoot className={className} containerClassName={containerClassName}>
      {!!withHeader && (
        <TableHeader
          columns={columns}
          sort={sort}
          onSort={onSort}
          headersClassName={headersClassName}
          stickyHeader={stickyHeader}
          readonly={readonly || isEmpty}
        />
      )}
      {loading ? (
        <TableLoader columnsCount={columns.length} />
      ) : isEmpty && Boolean(emptyMessage) ? (
        <TableEmptyState columnsCount={columns.length}>
          {emptyMessage}
        </TableEmptyState>
      ) : (
        <TableBody
          data={data}
          columns={columns}
          clampCells={clampCells}
          verticalAlign={verticalAlign}
          cellsClassName={cellsClassName}
          cellsContentClassName={cellsContentClassName}
          onRowClick={onRowClick}
          getRowKey={getRowKey}
          getRowProps={getRowProps}
          getCellProps={getCellProps}
        />
      )}
    </TableRoot>
  );
};

export default React.memo(TableV2) as typeof TableV2;
